import { memo } from 'react';
import { CameraOutlineIcon } from '@/assets/icons';
import { HeartOutlineIcon } from '@/assets/icons';
import { LockIcon } from '@/assets/icons';
import { sanitizeUserInput, formatPriceWithCurrency } from '@/lib';
import { Card } from '@/components/ui/card';
import type { WishlistItem } from '../../model';
import { DEFAULT_CURRENCY, DEFAULT_GIFT_TAG } from '../../config';
import { getGiftTagLabel, getGiftTagEmoji } from '../../lib';

interface WishlistItemCardProps {
  item: WishlistItem;
  onClick?: () => void;
  t: (key: string) => string; // Функция перевода передается извне
  /** Счётчик копирований (отображается рядом с ценой) */
  copyCount?: number;
  /** Скрыть бейдж метки (gift tag) на карточке */
  hideGiftTag?: boolean;
  /** Бейдж «Реклама» — для рекламных товаров из подборок */
  isSponsored?: boolean;
  /** Забронировано ли желание (есть purchasedBy) */
  isBooked?: boolean;
  /** Может ли текущий пользователь видеть замок (владелец или друг) */
  canSeeLock?: boolean;
}

export const WishlistItemCard = memo(function WishlistItemCard({ item, onClick, t, copyCount, hideGiftTag, isSponsored, isBooked, canSeeLock }: WishlistItemCardProps): JSX.Element {
  // Вычисляем эмодзи метки один раз
  const tagEmoji = getGiftTagEmoji(item.giftTag);
  
  // Форматируем цену с символом валюты
  const formattedPrice = formatPriceWithCurrency(item.price, item.currency || DEFAULT_CURRENCY);

  // Показывать счётчик копирований
  const showCopyCount = copyCount !== undefined && copyCount > 0;

  return (
    <Card
      className="gap-0 overflow-hidden w-full"
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      {/* Изображение желания */}
      <div className="relative w-full aspect-square bg-muted overflow-hidden">
        {item.imageUrl ? (
          <img 
            src={item.imageUrl} 
            alt={item.title}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <CameraOutlineIcon className="w-12 h-12" style={{ color: 'var(--muted-icon)' }} />
          </div>
        )}

        {/* Оверлеи поверх фото: gift tag / реклама + замок бронирования */}
        <div className="absolute top-2 right-2 flex items-center gap-1.5">
          {/* Gift Tag badge */}
          {!hideGiftTag && item.giftTag && item.giftTag !== DEFAULT_GIFT_TAG && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-white/90 backdrop-blur-sm text-primary text-xs font-normal shadow-sm">
              {tagEmoji && <span className="mr-1">{tagEmoji}</span>}
              {getGiftTagLabel(item.giftTag, t)}
            </span>
          )}

          {/* Бейдж «Реклама» — для спонсорских товаров из подборок */}
          {isSponsored && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/90 backdrop-blur-sm text-amber-700 text-xs font-normal shadow-sm">
              {t('common:sponsoredBadge')}
            </span>
          )}

          {/* Замок — видно только владельцу и друзьям когда желание забронировано */}
          {isBooked && canSeeLock && (
            <div
              className="bg-white/90 backdrop-blur-sm rounded-full w-6 h-6 flex items-center justify-center shadow-sm"
              aria-label="Забронировано"
            >
              <LockIcon size={12} style={{ color: 'var(--blue-dark)' }} />
            </div>
          )}
        </div>
      </div>

      {/* Контент желания */}
      <div className="flex flex-col gap-1 px-4 py-2">
        {/* Название */}
        <h3 className="font-semibold text-sm leading-tight truncate">
          {sanitizeUserInput(item.title)}
        </h3>

        {/* Цена и счётчик копирований */}
        <div className="flex items-center gap-2 overflow-hidden h-5">
          <span className="text-sm font-normal text-muted-foreground truncate min-w-0">
            {formattedPrice || t('wishlist:wishlist.card.noPrice')}
          </span>

          {showCopyCount && (
            <div className="flex items-center gap-1 text-muted-foreground shrink-0">
              <span>·</span>
              <HeartOutlineIcon className="w-3.5 h-3.5" />
              <span className="text-xs">{copyCount}</span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
});